import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Construction", item: "https://cashmyproperty.ai/services/construction" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is PMC and do I really need it?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "PMC (Project Management Consultancy) is professional oversight of your construction project. The PMC ensures the contractor follows the approved design, uses specified materials, stays on timeline, and adheres to the budget. Without PMC, cost overruns of 25–40% are common, timelines slip by months, and quality issues are discovered only after completion. PMC typically saves 3–5x its fee through cost control and rework prevention.",
      },
    },
    {
      "@type": "Question",
      name: "How do you select contractors for my project?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We run a competitive bidding process. Based on your approved design and BOQ (Bill of Quantities), we invite bids from 3–5 pre-qualified contractors with relevant experience. Each bid is scored on price, past project quality, timeline commitment, and financial stability. You make the final selection with our recommendation. This process typically yields 10–18% cost savings versus single-source negotiation.",
      },
    },
    {
      "@type": "Question",
      name: "How are cost overruns prevented?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Three layers of cost control: (1) Detailed BOQ with quantities and rates locked before construction starts, (2) milestone-based payment release — contractor gets paid only for completed and verified work, and (3) monthly cost reconciliation against budget with variance reports. Any deviation above 5% requires your approval. Our projects average under 5% cost variance compared to the industry average of 20–30%.",
      },
    },
    {
      "@type": "Question",
      name: "What types of construction projects do you manage?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We manage residential projects (individual homes, villas, apartments up to G+4), commercial projects (office buildings, retail spaces, warehouses), and mixed-use developments. Our sweet spot is projects with a construction budget of ₹50 lakh to ₹25 crore. For larger projects, we bring in specialist partners from our network.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Design & Feasibility",
    desc: "Architect engagement, concept design, structural design, and BOQ preparation. We ensure the design maximizes FSI utilization while staying within your budget. 3D visualization included so you see exactly what you are building before a single brick is laid.",
    icon: "ri-pencil-ruler-2-line",
  },
  {
    step: "02",
    title: "Contractor Bidding & Selection",
    desc: "We prepare a detailed tender document with BOQ, specifications, timeline, and payment terms. Shortlisted contractors submit sealed bids. We score each bid on price, quality track record, and financial capacity. You select the winner with full transparency.",
    icon: "ri-survey-line",
  },
  {
    step: "03",
    title: "Approvals & Clearances",
    desc: "Building plan sanction, BBMP/BDA/BMRDA approvals, RERA registration (if applicable), environmental clearance, labor cess registration, and utility connections. We handle the complete approval chain so construction starts without legal hurdles.",
    icon: "ri-file-check-line",
  },
  {
    step: "04",
    title: "Construction Execution & Monitoring",
    desc: "Full-time site supervision, weekly progress reports with photo documentation, material quality testing, and milestone verification. Payment released only against verified work completion. You receive a live dashboard with progress photos and schedule updates.",
    icon: "ri-tools-line",
  },
  {
    step: "05",
    title: "Quality Audit & Handover",
    desc: "Pre-handover snagging, quality audit against specifications, occupancy certificate procurement, and formal handover with complete documentation — warranties, as-built drawings, material certificates, and maintenance manuals. 12-month defect liability period included.",
    icon: "ri-check-double-line",
  },
];

const benefits = [
  { icon: "ri-money-rupee-circle-line", title: "10–18% Cost Savings", desc: "Competitive bidding with 3–5 pre-qualified contractors consistently delivers 10–18% savings versus single-vendor negotiation. Our average cost variance is under 5%." },
  { icon: "ri-timer-line", title: "On-Time Delivery", desc: "Milestone-linked schedules with penalty clauses for delays. Weekly monitoring catches slippage early. 90%+ of our projects complete within 10% of the original timeline." },
  { icon: "ri-shield-check-line", title: "Quality Assurance", desc: "Third-party material testing, stage-wise quality audits, and full-time site supervision. We catch quality issues during construction — not after handover when they are expensive to fix." },
  { icon: "ri-file-text-line", title: "Complete Documentation", desc: "As-built drawings, material certificates, warranty cards, and maintenance schedules — all organized and handed over in a digital format. Never hunt for a document again." },
  { icon: "ri-eye-2-line", title: "Live Project Dashboard", desc: "24/7 access to progress photos, schedule tracker, cost variance reports, and milestone status. Know exactly where your project stands — from anywhere in the world." },
  { icon: "ri-shield-star-line", title: "12-Month Defect Liability", desc: "Every contractor agreement includes a 12-month defect liability period. Any workmanship or material defect discovered post-handover is rectified at zero additional cost to you." },
];

const faqs = [
  { q: "What is PMC and do I really need it?", a: "PMC (Project Management Consultancy) is professional oversight of your construction project. The PMC ensures the contractor follows the approved design, uses specified materials, stays on timeline, and adheres to the budget. Without PMC, cost overruns of 25–40% are common, timelines slip by months, and quality issues are discovered only after completion. PMC typically saves 3–5x its fee through cost control and rework prevention." },
  { q: "How do you select contractors for my project?", a: "We run a competitive bidding process. Based on your approved design and BOQ (Bill of Quantities), we invite bids from 3–5 pre-qualified contractors with relevant experience. Each bid is scored on price, past project quality, timeline commitment, and financial stability. You make the final selection with our recommendation. This process typically yields 10–18% cost savings versus single-source negotiation." },
  { q: "How are cost overruns prevented?", a: "Three layers of cost control: (1) Detailed BOQ with quantities and rates locked before construction starts, (2) milestone-based payment release — contractor gets paid only for completed and verified work, and (3) monthly cost reconciliation against budget with variance reports. Any deviation above 5% requires your approval. Our projects average under 5% cost variance compared to the industry average of 20–30%." },
  { q: "What types of construction projects do you manage?", a: "We manage residential projects (individual homes, villas, apartments up to G+4), commercial projects (office buildings, retail spaces, warehouses), and mixed-use developments. Our sweet spot is projects with a construction budget of ₹50 lakh to ₹25 crore. For larger projects, we bring in specialist partners from our network." },
];

export default function ConstructionPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Construction Management — PMC, Contractor Selection & Execution Monitoring | CashMyProperty.ai",
    description: "Build your project on time, on budget, and to specification. Full PMC services including design coordination, competitive contractor bidding, approvals management, site supervision, quality audits, and handover documentation. Live project dashboard. 10–18% cost savings through competitive bidding. 12-month defect liability.",
    canonicalUrl: "https://cashmyproperty.ai/services/construction",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20construction%20project%20manager%20with%20hard%20hat%20reviewing%20architectural%20blueprints%20at%20active%20construction%20site%2C%20modern%20building%20under%20construction%2C%20warm%20golden%20hour%20sunlight%2C%20India%20construction%20industry%2C%20cinematic%20photography%2C%20deep%20teal%20and%20gold%20palette%2C%20high%20detail%2C%20professional%20supervision%20aesthetic&width=1800&height=800&seq=service-construction-hero&orientation=landscape"
          alt="Construction Management — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Construction</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Management</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              PMC, contractor selection, and execution monitoring. Build on time, on budget, and to specification — without the stress.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Construction</span>
          </nav>
        </div>
      </section>

      {/* What is Construction Management */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Professional Project Leadership</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Stop Losing Sleep Over Your Construction Project
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                If you have ever managed a construction project — or heard horror stories from someone who has — you know the drill: cost overruns, timeline slips, quality compromises, and endless contractor disputes. The average Indian construction project runs <strong className="text-foreground-950">20–30% over budget</strong> and 3–6 months behind schedule.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Our PMC (Project Management Consultancy) service puts a <strong className="text-foreground-950">professional team between you and the chaos</strong>. We handle design coordination, competitive contractor bidding, approvals, daily site supervision, quality testing, and milestone-based payment management.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                You get a <strong className="text-foreground-950">live dashboard</strong> with progress photos, schedule updates, and cost variance reports — accessible from anywhere. Your project, under control, without you needing to be on site every day.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Construction%20project%20manager%20reviewing%20tablet%20dashboard%20with%20building%20progress%20photos%2C%20modern%20construction%20site%20in%20background%2C%20warm%20natural%20lighting%2C%20professional%20oversight%20aesthetic%2C%20India%20real%20estate%20construction%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20tones%2C%20clean%20professional%20mood&width=800&height=600&seq=construction-pmc-01&orientation=landscape"
                alt="Construction PMC"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">From Blueprint to Handover</h2>
            <p className="text-foreground-600">Five stages. Complete oversight. Zero surprises.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What We Build</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Project Types We Manage</h2>
            <p className="text-foreground-600">Residential, commercial, and mixed-use — ₹50 lakh to ₹25 crore budget range.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: "ri-home-2-line", title: "Individual Homes & Villas", desc: "Custom-designed independent houses and luxury villas. Complete PMC from architect selection to handover keys.", budget: "₹50 L – ₹3 Cr" },
              { icon: "ri-building-line", title: "Apartment Buildings", desc: "G+3 to G+4 apartment projects. FSI optimization, contractor bidding, multi-unit quality control, and OC procurement.", budget: "₹2 Cr – ₹15 Cr" },
              { icon: "ri-store-2-line", title: "Commercial Buildings", desc: "Office spaces, retail complexes, and mixed-use developments. Compliance with commercial building codes and fire safety norms.", budget: "₹1 Cr – ₹25 Cr" },
              { icon: "ri-archive-line", title: "Warehouses & Industrial", desc: "Logistics warehouses, cold storage, and light industrial facilities. Optimized for functionality and cost-efficiency.", budget: "₹75 L – ₹10 Cr" },
              { icon: "ri-building-4-line", title: "Redevelopment Projects", desc: "Demolition, consent management, and construction of replacement buildings. Society redevelopment specialists.", budget: "₹3 Cr – ₹20 Cr" },
              { icon: "ri-hotel-line", title: "Hospitality & Resorts", desc: "Boutique hotels, eco-resorts, and farm stays. Design-build with hospitality-grade finishing and amenity integration.", budget: "₹1 Cr – ₹15 Cr" },
            ].map((proj) => (
              <div key={proj.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${proj.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{proj.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{proj.desc}</p>
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary-500/10 text-primary-700">{proj.budget}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">What Our PMC Delivers</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "150+", label: "Projects Managed" },
              { value: "10–18%", label: "Cost Savings vs Single Vendor" },
              { value: "&lt;5%", label: "Average Budget Variance" },
              { value: "90%+", label: "On-Time Completion Rate" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Build With Confidence?
          </h2>
          <p className="text-foreground-600 mb-8">
            Share your project brief. We will prepare a free PMC proposal with cost estimates and timeline projections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Request PMC Proposal
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Get AI Report — ₹4,999
              <i className="ri-file-chart-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}